/**
 * Leading Candidate Calculation Utilities
 *
 * Determines which candidate is "leading" in a race for summary display.
 * Also provides shared top-two filtering logic used by multiple components.
 */

import type { CandidateTableRow, LeadingCandidateInfo } from '../types/election.types';

/**
 * Filter candidates to the top two (leading Democrat and Republican) using
 * API-provided `leadingDemConid` / `leadingRepConid`.
 * Falls back to first two by API order when the leading conids are absent or
 * don't match any candidate in the list.
 */
export function filterTopTwoCandidates<T extends { conid_yes?: number; conid?: number }>(
  candidates: T[],
  leadingDemConid: number | undefined,
  leadingRepConid: number | undefined,
): T[] {
  if (candidates.length <= 2) return candidates;

  if (leadingDemConid || leadingRepConid) {
    const topTwo = candidates.filter((c) => {
      const id = ('conid_yes' in c ? c.conid_yes : c.conid) as number | undefined;
      return id === leadingDemConid || id === leadingRepConid;
    });
    // Primaries: only one leading conid matches — add next-best to ensure 2
    if (topTwo.length === 1) {
      const leadingId = ('conid_yes' in topTwo[0] ? topTwo[0].conid_yes : topTwo[0].conid) as number | undefined;
      const second = candidates.find((c) => {
        const id = ('conid_yes' in c ? c.conid_yes : c.conid) as number | undefined;
        return id !== leadingId;
      });
      if (second) topTwo.push(second);
    }
    if (topTwo.length > 0) return topTwo;
  }

  // Fallback: first two from API order
  return candidates.slice(0, 2);
}

/**
 * Calculate the leading candidate from a list of candidates in a race.
 *
 * Current algorithm: Highest price wins
 */
export function calculateLeadingCandidate(candidateRows: CandidateTableRow[]): LeadingCandidateInfo {
  if (candidateRows.length === 0) {
    throw new Error('Cannot calculate leading candidate from empty candidate list');
  }

  // Find candidate with highest price
  const leadingCandidate = candidateRows.reduce((leading, current) => {
    return current.price > leading.price ? current : leading;
  });

  return {
    name: leadingCandidate.candidateName,
    party: leadingCandidate.party,
    partyAffiliation: leadingCandidate.partyAffiliation,
    price: leadingCandidate.price,
    conid: leadingCandidate.conid_yes,
  };
}
